import { useEffect } from "react";

/**
 * Keeps the address bar on the canonical due diligence URL — the one built on the
 * case alias ("DD-2026-12345678"), which is the reference the client sees on the
 * report and reads out over the phone.
 *
 * Old links, emails and bookmarks still carry the case UUID. The API takes either
 * one, so those pages load fine; this only rewrites the URL once the case is in,
 * which is the moment its alias is known.
 *
 * It swaps the URL through the History API instead of `navigate(..., { replace: true })`
 * on purpose: a router navigation changes the route param, and the param is what
 * every fetch on these pages keys on, so the redirect would reload the whole report
 * it just finished loading. replaceState is our 301: it drops the id URL from the
 * history instead of stacking a second entry the back button would land on.
 *
 * @param urlAlias The case identifier as it came in the URL — the alias, or the
 *   UUID when the visitor arrived through an old link.
 * @param caseAlias The loaded case's alias, once known.
 */
export const useCanonicalCaseUrl = (
  urlAlias: string | undefined,
  caseAlias: string | undefined
) => {
  useEffect(() => {
    if (!urlAlias || !caseAlias || urlAlias === caseAlias) return;

    const { pathname, search, hash } = window.location;
    const canonical = pathname.replace(encodeURIComponent(urlAlias), encodeURIComponent(caseAlias));
    if (canonical === pathname) return;

    window.history.replaceState(window.history.state, "", `${canonical}${search}${hash}`);
  }, [urlAlias, caseAlias]);
};
